import { createFileRoute, useNavigate, Link } from '@tanstack/react-router'
import { useMemo, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import {
  TrendingUp,
  TrendingDown,
  ShoppingCart,
  DollarSign,
  Package,
  Filter,
  Users,
  Heart,
  MessageCircle,
  UserCircle2,
  AlertTriangle,
  CalendarDays,
  CalendarClock,
  Clock3,
  UserCheck,
  RefreshCw,
  Radio,
} from 'lucide-react'
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts'
import { useOrderAnalyticsSummary, useOrderAnalyticsOverview, useOrderTopProducts, useOrderFunnel, useOrderCohorts } from '@/hooks/data/useOrderAnalytics'
import { useUsers } from '@/hooks/data/useUsers'
import { useTopWishlisted } from '@/lib/wishlist'
import { useProductListAdmin } from '@/lib/productCatalog'
import { useOrderTracking } from '@/hooks/useOrderTracking'
import type { OrderStatusChangedPayload } from '@/hooks/useOrderTracking'
import { useOrdersList } from '../../../hooks/data/useOrders'
import { useInbox } from '../../../hooks/data/useMessages'
import type { OrderSummary } from '@/types/ecommerce'
import { useAuthStore } from '@/hooks/auth/useAuthStore'
import { useSubscriptionDashboard, useAllTenantSubscriptions } from '@/hooks/data/useSubscriptions'
import { Building2, CreditCard } from 'lucide-react'

const nzd = new Intl.NumberFormat('en-NZ', { style: 'currency', currency: 'NZD' })

const STATUS_COLORS: Record<string, string> = {
  Pending: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  Processing: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  Completed: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
  Cancelled: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
  Refunded: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
}

// Same semantic colors as STATUS_COLORS' pill backgrounds, as flat hex for the recharts donut
// (recharts fills need a real color value, not a Tailwind class).
const STATUS_HEX: Record<string, string> = {
  Pending: '#f59e0b',
  Processing: '#2563eb',
  Completed: '#16a34a',
  Cancelled: '#dc2626',
  Refunded: '#9ca3af',
}

function StatCard({
  icon: Icon,
  label,
  value,
  deltaPercent,
}: {
  icon: typeof TrendingUp
  label: string
  value: string
  /** Period-over-period % change, e.g. from OrderAnalyticsSummary.revenueChangePercent. Omit
   * or pass null/undefined when there's no prior-period baseline to compare against. */
  deltaPercent?: number | null
}) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-800">
      <div className="flex items-center gap-3">
        <div className="rounded-lg bg-blue-50 p-2.5 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400">
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
          <div className="flex items-center gap-2">
            <p className="text-lg font-semibold text-gray-900 dark:text-white">{value}</p>
            {deltaPercent != null && (
              <span
                className={`flex items-center gap-0.5 text-xs font-medium ${
                  deltaPercent >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                }`}
              >
                {deltaPercent >= 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                {Math.abs(deltaPercent)}%
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

function retentionColor(pct: number | null) {
  if (pct == null) return 'bg-gray-50 dark:bg-gray-900/40'
  if (pct >= 66) return 'bg-green-600 text-white'
  if (pct >= 40) return 'bg-green-400 text-white dark:bg-green-600'
  if (pct >= 20) return 'bg-green-200 text-green-900 dark:bg-green-800 dark:text-green-100'
  if (pct > 0) return 'bg-green-50 text-green-800 dark:bg-green-900/40 dark:text-green-300'
  return 'bg-gray-50 text-gray-400 dark:bg-gray-900/40 dark:text-gray-500'
}

function FunnelSection({ days }: { days: number }) {
  const funnel = useOrderFunnel(days)

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-800">
      <h2 className="mb-1 flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-white">
        <Filter className="h-5 w-5" /> Order funnel
      </h2>
      <p className="mb-4 text-xs text-gray-500 dark:text-gray-400">
        Real order-lifecycle stages from OrderHistory — not page views (this system doesn't track those).
      </p>
      {funnel.isLoading ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : !funnel.data || funnel.data.stages[0]?.count === 0 ? (
        <p className="text-sm text-gray-500">No orders in this window.</p>
      ) : (
        <div className="space-y-2">
          {funnel.data.stages.map((stage, i) => {
            const placed = funnel.data!.stages[0].count
            const pct = placed > 0 ? Math.round((stage.count / placed) * 100) : 0
            const prev = i > 0 ? funnel.data!.stages[i - 1].count : stage.count
            const dropOff = prev > 0 ? Math.round(((prev - stage.count) / prev) * 100) : 0
            return (
              <div key={stage.stage}>
                <div className="mb-1 flex items-center justify-between text-sm">
                  <span className="font-medium text-gray-800 dark:text-gray-100">{stage.stage}</span>
                  <span className="text-gray-500 dark:text-gray-400">
                    {stage.count} ({pct}%)
                    {i > 0 && dropOff > 0 && <span className="ml-2 text-red-500">−{dropOff}%</span>}
                  </span>
                </div>
                <div className="h-2.5 w-full overflow-hidden rounded-full bg-gray-100 dark:bg-gray-700">
                  <div className="h-full rounded-full bg-blue-600" style={{ width: `${pct}%` }} />
                </div>
              </div>
            )
          })}
          <div className="mt-3 border-t border-gray-100 pt-3 text-sm text-gray-500 dark:border-gray-700 dark:text-gray-400">
            Cancelled: <span className="font-medium text-red-500">{funnel.data.cancelledCount}</span>
          </div>
        </div>
      )}
    </div>
  )
}

function CohortSection() {
  const [months, setMonths] = useState(6)
  const cohorts = useOrderCohorts(months)

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-800">
      <div className="mb-1 flex items-center justify-between">
        <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-white">
          <Users className="h-5 w-5" /> Retention cohorts
        </h2>
        <select
          value={months}
          onChange={(e) => setMonths(Number(e.target.value))}
          className="rounded-lg border border-gray-300 px-2 py-1 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-white"
        >
          <option value={3}>Last 3 months</option>
          <option value={6}>Last 6 months</option>
          <option value={12}>Last 12 months</option>
        </select>
      </div>
      <p className="mb-4 text-xs text-gray-500 dark:text-gray-400">
        % of each first-order cohort that placed another order N months later. Real data from OrderService — blank cells haven't happened yet.
      </p>
      {cohorts.isLoading ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : !cohorts.data || cohorts.data.cohorts.length === 0 ? (
        <p className="text-sm text-gray-500">Not enough order history yet to build cohorts.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="text-sm">
            <thead>
              <tr className="text-left text-xs uppercase text-gray-500 dark:text-gray-400">
                <th className="pb-2 pr-4">Cohort</th>
                <th className="pb-2 pr-4">Size</th>
                {Array.from({ length: cohorts.data.monthsTracked + 1 }).map((_, i) => (
                  <th key={i} className="px-1 pb-2 text-center">
                    M{i}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {cohorts.data.cohorts.map((row) => (
                <tr key={row.cohortMonth} className="border-t border-gray-100 dark:border-gray-700">
                  <td className="py-1.5 pr-4 font-medium text-gray-800 dark:text-gray-100">{row.cohortMonth}</td>
                  <td className="py-1.5 pr-4 text-gray-500 dark:text-gray-400">{row.cohortSize}</td>
                  {Array.from({ length: cohorts.data!.monthsTracked + 1 }).map((_, i) => {
                    const pct = row.retentionByMonthOffset[i] ?? null
                    return (
                      <td key={i} className="p-0.5">
                        <div className={`flex h-9 w-14 items-center justify-center rounded text-xs font-medium ${retentionColor(pct)}`}>
                          {pct != null ? `${pct}%` : ''}
                        </div>
                      </td>
                    )
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

function TopWishlistedSection() {
  const wishlisted = useTopWishlisted(8)

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-800">
      <h2 className="mb-4 flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-white">
        <Heart className="h-5 w-5" /> Most wishlisted
      </h2>
      {wishlisted.isLoading ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : !wishlisted.data || wishlisted.data.length === 0 ? (
        <p className="text-sm text-gray-500">No products saved to a wishlist yet.</p>
      ) : (
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase text-gray-500 dark:text-gray-400">
            <tr>
              <th className="pb-2">Product</th>
              <th className="pb-2">SKU</th>
              <th className="pb-2">Saves</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
            {wishlisted.data.map((p) => (
              <tr key={p.productId}>
                <td className="py-2 text-gray-900 dark:text-white">{p.name}</td>
                <td className="py-2 text-gray-500">{p.sku}</td>
                <td className="py-2">{p.wishlistCount}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}

/**
 * Products at or below their reorder threshold — `minStockLevel` now comes straight off
 * ProductSummaryDto (added for this), so this is a single list call filtered client-side
 * rather than an N-detail-call fan-out. pageSize:100 covers the current catalog in one
 * request; a store with a much larger catalog would want a real server-side
 * "low stock only" filter instead; not needed at today's scale.
 */
function LowStockSection() {
  const products = useProductListAdmin({ pageSize: 100 })
  const navigate = useNavigate()

  const lowStock = useMemo(
    () =>
      (products.data?.items ?? [])
        .filter((p) => p.quantity <= p.minStockLevel)
        .sort((a, b) => a.quantity - b.quantity)
        .slice(0, 8),
    [products.data],
  )

  if (products.isLoading || lowStock.length === 0) return null

  return (
    <div className="rounded-xl border border-red-200 bg-red-50/50 p-5 dark:border-red-900 dark:bg-red-900/10">
      <div className="mb-1 flex items-center justify-between">
        <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-white">
          <AlertTriangle className="h-5 w-5 text-red-500" /> Low stock
        </h2>
        <button onClick={() => navigate({ to: '/inventory' })} className="text-sm text-primary-600 hover:underline dark:text-primary-400">
          View inventory →
        </button>
      </div>
      <p className="mb-4 text-xs text-gray-500 dark:text-gray-400">Products at or below their reorder threshold.</p>
      <table className="w-full text-sm">
        <thead className="text-left text-xs uppercase text-gray-500 dark:text-gray-400">
          <tr>
            <th className="pb-2">Product</th>
            <th className="pb-2">SKU</th>
            <th className="pb-2">Quantity</th>
            <th className="pb-2">Reorder at</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-red-100 dark:divide-red-900/40">
          {lowStock.map((p) => (
            <tr key={p.id}>
              <td className="py-2 font-medium text-gray-900 dark:text-white">{p.name}</td>
              <td className="py-2 text-gray-500">{p.sku}</td>
              <td className="py-2 font-semibold text-red-600 dark:text-red-400">{p.quantity}</td>
              <td className="py-2 text-gray-500">{p.minStockLevel}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function relativeTime(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime()
  const mins = Math.round(diffMs / 60_000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hours = Math.round(mins / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.round(hours / 24)
  return `${days}d ago`
}

/**
 * Per-customer rollup of order + payment + chat activity, the three things a manager
 * otherwise has to check on three separate pages (Orders, Billing, Messages) with no
 * shared "pick a customer" starting point. Built entirely client-side from data that's
 * already real and already fetched elsewhere in the app — no new backend aggregate:
 *  - orders: OrderService's staff-wide list (customerId added to OrderSummaryDto for
 *    exactly this — it used to only exist on the single-order detail response)
 *  - chat: CrmService's inbox, already one conversation per customer
 *  - identity/name: IdentityService's user list, filtered to role=customer
 * "Payment" here is each customer's most recent order's real paymentStatus/total — there's
 * no separate Payment-list-by-customer endpoint in BillingService (only create/refund
 * exist), so order-level payment status is the honest signal to show, not a fabricated one.
 * Only customers with at least one real order or conversation are shown — not all 36
 * registered accounts, most of which are test/seed junk with zero activity.
 */
function CustomerActivitySection() {
  const orders = useOrdersList({ pageSize: 100 })
  const inbox = useInbox()
  const customers = useUsers('customer')
  const navigate = useNavigate()

  const rows = useMemo(() => {
    const lastOrderByCustomer = new Map<string, OrderSummary>()
    const orderCountByCustomer = new Map<string, number>()
    for (const o of orders.data?.items ?? []) {
      orderCountByCustomer.set(o.customerId, (orderCountByCustomer.get(o.customerId) ?? 0) + 1)
      const existing = lastOrderByCustomer.get(o.customerId)
      if (!existing || new Date(o.createdAt) > new Date(existing.createdAt)) lastOrderByCustomer.set(o.customerId, o)
    }

    const conversationByCustomer = new Map((inbox.data ?? []).map((c) => [c.customerUserId, c]))
    const userById = new Map((customers.data ?? []).map((u) => [u.id, u]))

    const customerIds = new Set<string>([...lastOrderByCustomer.keys(), ...conversationByCustomer.keys()])

    return Array.from(customerIds)
      .map((id) => {
        const lastOrder = lastOrderByCustomer.get(id)
        const conversation = conversationByCustomer.get(id)
        const user = userById.get(id)
        const lastActivity = Math.max(
          lastOrder ? new Date(lastOrder.createdAt).getTime() : 0,
          conversation ? new Date(conversation.lastMessageAt).getTime() : 0,
        )
        return {
          id,
          label: user?.name || user?.email || `Customer ${id.slice(0, 8)}`,
          orderCount: orderCountByCustomer.get(id) ?? 0,
          lastOrder,
          conversation,
          lastActivity,
        }
      })
      .sort((a, b) => b.lastActivity - a.lastActivity)
      .slice(0, 12)
  }, [orders.data, inbox.data, customers.data])

  const loading = orders.isLoading || inbox.isLoading || customers.isLoading

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-800">
      <h2 className="mb-1 flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-white">
        <UserCircle2 className="h-5 w-5" /> Customer activity
      </h2>
      <p className="mb-4 text-xs text-gray-500 dark:text-gray-400">
        Orders, payment status and chat together per customer — click a customer's order or chat to jump straight there.
      </p>

      {loading ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-gray-500">No customer orders or conversations yet.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[720px] text-sm">
            <thead className="text-left text-xs uppercase text-gray-500 dark:text-gray-400">
              <tr>
                <th className="pb-2 pr-4">Customer</th>
                <th className="pb-2 pr-4">Orders</th>
                <th className="pb-2 pr-4">Last order</th>
                <th className="pb-2 pr-4">Payment</th>
                <th className="pb-2 pr-4">Chat</th>
                <th className="pb-2">Activity</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {rows.map((row) => (
                <tr key={row.id}>
                  <td className="py-2.5 pr-4 font-medium text-gray-900 dark:text-white">{row.label}</td>
                  <td className="py-2.5 pr-4 text-gray-600 dark:text-gray-300">{row.orderCount}</td>
                  <td className="py-2.5 pr-4">
                    {row.lastOrder ? (
                      <button
                        type="button"
                        onClick={() => navigate({ to: '/orders/$id', params: { id: row.lastOrder!.id } })}
                        className="flex items-center gap-2 text-left hover:underline"
                      >
                        <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_COLORS[row.lastOrder.status] ?? ''}`}>
                          {row.lastOrder.status}
                        </span>
                        <span className="text-gray-600 dark:text-gray-300">{nzd.format(row.lastOrder.total)}</span>
                      </button>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>
                  <td className="py-2.5 pr-4 text-gray-600 dark:text-gray-300">
                    {row.lastOrder ? row.lastOrder.paymentStatus : <span className="text-gray-400">—</span>}
                  </td>
                  <td className="py-2.5 pr-4">
                    {row.conversation ? (
                      <Link
                        to="/messages"
                        search={{ conversationId: row.conversation.id } as never}
                        className="flex items-center gap-1.5 text-primary-700 hover:underline dark:text-primary-400"
                      >
                        <MessageCircle className="h-3.5 w-3.5 shrink-0" />
                        <span className="max-w-[160px] truncate">{row.conversation.lastMessagePreview || 'No messages'}</span>
                        {row.conversation.unreadCount > 0 && (
                          <span className="flex h-4 min-w-4 shrink-0 items-center justify-center rounded-full bg-blue-600 px-1 text-[10px] font-semibold text-white">
                            {row.conversation.unreadCount}
                          </span>
                        )}
                      </Link>
                    ) : (
                      <span className="text-gray-400">No chat</span>
                    )}
                  </td>
                  <td className="py-2.5 text-xs text-gray-400">{relativeTime(new Date(row.lastActivity).toISOString())}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

/** Today/this-week/pending/active-customer KPI row — see OrdersController.AnalyticsOverview.
 * Independent of the page's `days` window selector on purpose: "today" and "this week" are
 * fixed calendar boundaries, not another 7/30/90-day slider position. */
function OverviewSection() {
  const overview = useOrderAnalyticsOverview()

  return (
    <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
      <StatCard
        icon={CalendarDays}
        label="Today's orders"
        value={overview.data ? `${overview.data.todayOrders} · ${nzd.format(overview.data.todayRevenue)}` : '—'}
      />
      <StatCard
        icon={CalendarClock}
        label="This week's orders"
        value={overview.data ? `${overview.data.weekOrders} · ${nzd.format(overview.data.weekRevenue)}` : '—'}
      />
      <StatCard
        icon={Clock3}
        label="Pending orders"
        value={overview.data ? String(overview.data.pendingOrders) : '—'}
      />
      <StatCard
        icon={UserCheck}
        label="Active customers (30d)"
        value={overview.data ? `${overview.data.activeCustomers} / ${overview.data.totalCustomers}` : '—'}
      />
    </div>
  )
}

/**
 * Live feed of order status changes pushed by OrderService's OrderHub — the same
 * `OnOrderStatusChanged` event the root layout already uses to toast + bust order-list/detail
 * caches (see `(admin)/route.tsx`). A second, independent hub connection here (same pattern
 * `useOrderTracking` is designed for — see its doc comment) just to keep a local rolling log
 * for this page, capped at 10 so it reads as "recent", not a growing unbounded list.
 */
function RecentActivitySection() {
  const [events, setEvents] = useState<OrderStatusChangedPayload[]>([])
  const navigate = useNavigate()

  useOrderTracking((payload) => {
    setEvents((prev) => [payload, ...prev].slice(0, 10))
  })

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-800">
      <h2 className="mb-1 flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-white">
        <Radio className="h-4 w-4 text-green-500" /> Recent activity
      </h2>
      <p className="mb-4 text-xs text-gray-500 dark:text-gray-400">
        Live order status changes (OrderHub) since this page was opened — nothing to show until the next status change happens.
      </p>
      {events.length === 0 ? (
        <p className="text-sm text-gray-500">No order status changes yet this session.</p>
      ) : (
        <ul className="space-y-2">
          {events.map((e, i) => (
            <li key={`${e.orderId}-${e.at}-${i}`} className="flex items-center justify-between text-sm">
              <button
                type="button"
                onClick={() => navigate({ to: '/orders/$id', params: { id: e.orderId } })}
                className="flex items-center gap-2 text-left hover:underline"
              >
                <span className="font-medium text-gray-900 dark:text-white">{e.orderNumber}</span>
                <span className="text-gray-500 dark:text-gray-400">{e.previousStatus} →</span>
                <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_COLORS[e.newStatus] ?? ''}`}>{e.newStatus}</span>
              </button>
              <span className="shrink-0 text-xs text-gray-400">{relativeTime(e.at)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

/** Admin-only cross-tenant SaaS glance — the full MRR/churn/plan-distribution charts live on
 * /billing; this is just enough to surface "is the platform itself healthy" without leaving
 * the shared dashboard, same real data source (SubscriptionsController), no new endpoint. */
function SaasSnapshotCard() {
  const { data: dashboard } = useSubscriptionDashboard()
  const { data: tenants = [] } = useAllTenantSubscriptions()
  if (!dashboard) return null

  return (
    <Link
      to="/billing"
      className="block rounded-xl border border-gray-200 bg-white p-4 hover:border-blue-300 dark:border-gray-700 dark:bg-gray-800 dark:hover:border-blue-700"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm font-semibold text-gray-800 dark:text-white">
          <CreditCard className="h-4 w-4" /> SaaS snapshot
        </div>
        <span className="text-xs text-blue-600 dark:text-blue-400">Manage subscriptions →</span>
      </div>
      <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <div>
          <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400"><Building2 className="h-3 w-3" /> Tenants</div>
          <div className="text-lg font-bold text-gray-900 dark:text-white">{tenants.length}</div>
        </div>
        <div>
          <div className="text-xs text-gray-500 dark:text-gray-400">MRR</div>
          <div className="text-lg font-bold text-gray-900 dark:text-white">{nzd.format(dashboard.mrr)}</div>
        </div>
        <div>
          <div className="text-xs text-gray-500 dark:text-gray-400">Active subs</div>
          <div className="text-lg font-bold text-gray-900 dark:text-white">{dashboard.activeSubscriptions}</div>
        </div>
        <div>
          <div className="text-xs text-gray-500 dark:text-gray-400">Churn (30d)</div>
          <div className="text-lg font-bold text-gray-900 dark:text-white">{dashboard.churnRatePercent}%</div>
        </div>
      </div>
    </Link>
  )
}

export function EcommerceAnalyticsPage() {
  const [days, setDays] = useState(30)
  const [lastRefreshed, setLastRefreshed] = useState(() => new Date())
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const summary = useOrderAnalyticsSummary(days)
  const topProducts = useOrderTopProducts(8, days)
  const isAdmin = useAuthStore((s) => s.hasRole('admin'))

  const goToOrdersWithStatus = (status: string) => navigate({ to: '/orders', search: { status } as never })

  const refreshAll = () => {
    queryClient.invalidateQueries({ queryKey: ['order-analytics'] })
    setLastRefreshed(new Date())
  }

  return (
    <div className="p-2 md:p-4 space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
            <TrendingUp className="w-6 h-6" /> Ecommerce Analytics
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Real revenue and order data from OrderService — revenue is recognized on Stripe
            payment success (paymentStatus), not just order status.
          </p>
        </div>
        <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
          <span>Auto-refreshes every 30s · last updated {lastRefreshed.toLocaleTimeString()}</span>
          <button
            onClick={refreshAll}
            className="inline-flex items-center gap-1 rounded-lg border border-gray-300 px-2.5 py-1.5 font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
          >
            <RefreshCw className="h-3.5 w-3.5" /> Refresh
          </button>
        </div>
      </div>

      {isAdmin && <SaasSnapshotCard />}

      <OverviewSection />

      <div className="flex items-center gap-2">
        <label className="text-sm text-gray-600 dark:text-gray-400">
          Window
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="ml-2 rounded-lg border border-gray-300 px-2 py-1 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-white"
          >
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
        </label>
      </div>

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <StatCard
          icon={DollarSign}
          label="Revenue (paid)"
          value={summary.data ? nzd.format(summary.data.totalRevenue) : '—'}
          deltaPercent={summary.data?.revenueChangePercent}
        />
        <StatCard
          icon={ShoppingCart}
          label="Total orders"
          value={summary.data ? String(summary.data.totalOrders) : '—'}
        />
        <StatCard
          icon={TrendingUp}
          label="Avg order value"
          value={summary.data ? nzd.format(summary.data.averageOrderValue) : '—'}
        />
        <StatCard
          icon={Package}
          label="Top seller (units)"
          value={topProducts.data?.[0] ? String(topProducts.data[0].unitsSold) : '—'}
        />
      </div>

      <div className="rounded-xl border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-800">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Revenue by day</h2>
          {summary.data && (
            <span className="text-xs text-gray-500 dark:text-gray-400">
              GST collected: {nzd.format(summary.data.totalTax)}
            </span>
          )}
        </div>
        {summary.isLoading ? (
          <p className="text-sm text-gray-500">Loading…</p>
        ) : !summary.data || summary.data.revenueByDay.length === 0 ? (
          <p className="text-sm text-gray-500">No paid orders in this window.</p>
        ) : (
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={summary.data.revenueByDay}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" fontSize={12} />
              <YAxis fontSize={12} />
              <Tooltip formatter={(v) => nzd.format(Number(v))} />
              <Legend />
              <Line type="monotone" name="Revenue" dataKey="revenue" stroke="#2563eb" strokeWidth={2} dot={false} />
              <Line type="monotone" name="GST" dataKey="tax" stroke="#16a34a" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="rounded-xl border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-800">
          <h2 className="mb-1 text-lg font-semibold text-gray-900 dark:text-white">Orders by status</h2>
          <p className="mb-4 text-xs text-gray-500 dark:text-gray-400">Click a slice to jump to that filtered order list.</p>
          {summary.isLoading ? (
            <p className="text-sm text-gray-500">Loading…</p>
          ) : !summary.data || summary.data.statusBreakdown.length === 0 ? (
            <p className="text-sm text-gray-500">No orders in this window.</p>
          ) : (
            <div className="grid grid-cols-2 items-center gap-2">
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={summary.data.statusBreakdown}
                    dataKey="count"
                    nameKey="status"
                    innerRadius={45}
                    outerRadius={80}
                    paddingAngle={2}
                    onClick={(entry) => goToOrdersWithStatus((entry as unknown as { status: string }).status)}
                    cursor="pointer"
                  >
                    {summary.data.statusBreakdown.map((s) => (
                      <Cell key={s.status} fill={STATUS_HEX[s.status] ?? '#9ca3af'} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
              <div className="space-y-2">
                {summary.data.statusBreakdown.map((s) => (
                  <button
                    key={s.status}
                    onClick={() => goToOrdersWithStatus(s.status)}
                    className="flex w-full items-center justify-between rounded-lg px-1 py-0.5 hover:bg-gray-50 dark:hover:bg-gray-700/50"
                  >
                    <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${STATUS_COLORS[s.status] ?? ''}`}>
                      {s.status}
                    </span>
                    <span className="text-sm font-medium text-gray-900 dark:text-white">{s.count}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="rounded-xl border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-800">
          <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">Top products</h2>
          {topProducts.isLoading ? (
            <p className="text-sm text-gray-500">Loading…</p>
          ) : !topProducts.data || topProducts.data.length === 0 ? (
            <p className="text-sm text-gray-500">No sales in this window.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase text-gray-500 dark:text-gray-400">
                <tr>
                  <th className="pb-2">Product</th>
                  <th className="pb-2">SKU</th>
                  <th className="pb-2">Units sold</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {topProducts.data.map((p) => (
                  <tr key={p.productId}>
                    <td className="py-2 text-gray-900 dark:text-white">{p.name}</td>
                    <td className="py-2 text-gray-500">{p.sku}</td>
                    <td className="py-2">{p.unitsSold}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <FunnelSection days={days} />
        <RecentActivitySection />
      </div>

      <CohortSection />

      <TopWishlistedSection />

      <LowStockSection />

      <CustomerActivitySection />
    </div>
  )
}

export const Route = createFileRoute('/(admin)/analytics/ecommerce')({ component: EcommerceAnalyticsPage })
